"use client";

/**
 * Citation edit-history timeline.
 *
 * Extracted from `compass-intake.jsx:954-992` (`.cs-htl`, `.cs-hstage`,
 * `.cs-hdot`, `.cs-hstagehead` classes).
 */

import type { ReactNode } from "react";
import type { SrcCardSource } from "../data-display/SrcCard";
import { SrcCard } from "../data-display/SrcCard";
import type { ProvenanceType } from "../lib/provenance";

/* ---------- types ---------- */

export interface HistoryStage {
  /** Provenance type for the colored dot. */
  type: ProvenanceType;
  /** Stage headline — can be rich content (e.g. `<>Edited to <b>$5M</b> by you</>`). */
  head: ReactNode;
  /** Meta line below the headline. */
  meta: string;
  /** Attached reference cards. */
  refs?: SrcCardSource[];
  /** Optional context recorded with this change. */
  reason?: string;
  /** If true, the stage is shown slightly muted (prior revision). */
  prior?: boolean;
}

export interface CiteHistoryProps {
  /** Ordered timeline stages (newest first). */
  stages: HistoryStage[];
}

/* ---------- dot color map ---------- */

const DOT_BG: Record<string, string> = {
  provided: "bg-(--prov)",
  estimated: "bg-(--est)",
  catalog: "bg-(--accent-plum)",
  empty: "bg-(--empty)",
};

/**
 * History timeline — each stage shows a colored dot, headline + meta,
 * and optionally a list of reference cards.
 */
export function CiteHistory({ stages }: CiteHistoryProps) {
  return (
    <div className="py-2">
      {stages.map((st, i) => (
        <div
          key={i}
          className={["relative pb-[18px]", st.prior ? "opacity-70" : ""].join(
            " ",
          )}
        >
          {/* Vertical connector line */}
          {i < stages.length - 1 && (
            <span className="absolute left-[4px] top-[15px] bottom-0 w-[1.5px] bg-(--border-subtle)" />
          )}

          {/* Stage head row */}
          <div className="flex gap-3 items-start">
            <span
              className={[
                "w-[9px] h-[9px] rounded-full mt-[5px] shrink-0",
                "shadow-[0_0_0_3px_var(--bg-white)]",
                DOT_BG[st.type] ?? "bg-(--brand-navy)",
              ].join(" ")}
            />
            <div>
              <div className="text-[13px] text-(--fg-primary) leading-[1.4] [&_b]:font-bold">
                {st.head}
              </div>
              <div className="text-[11px] text-(--fg-muted) mt-[3px]">
                {st.meta}
              </div>
            </div>
          </div>

          {st.reason && (
            <div className="ml-[21px] mt-2 rounded-(--radius-field) border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2 text-[12px] leading-[1.45] text-(--fg-secondary)">
              {st.reason}
            </div>
          )}

          {/* References attached to this stage */}
          {st.refs && st.refs.length > 0 && (
            <div className="mt-[11px] ml-[21px]">
              {st.prior && (
                <div className="text-[10px] tracking-[.09em] uppercase text-(--fg-muted) font-semibold mb-[9px]">
                  {st.refs.length > 1 ? "References" : "Reference"} attached to
                  this value
                </div>
              )}
              <div className="flex flex-col gap-2">
                {st.refs.map((s, j) => (
                  <SrcCard
                    key={j}
                    source={s}
                    letter={String.fromCharCode(97 + j)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
